<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import GoogleSignInButton from '@/components/auth/GoogleSignInButton.vue'

const route = useRoute()
const auth = useAuthStore()

const pageTitle = computed(() => {
  if (route.path === '/game') return 'Crossword Generator'
  if (route.path === '/trivia') return 'Trivia Night'
  if (route.path === '/fantasy') return 'Fantasy Football'
  return 'Colin Lankau'
})

const isHomePage = computed(() => route.path === '/')
const isAuthCallback = computed(() => route.path === '/auth/callback')
</script>

<template>
  <header class="page-header">
    <div class="header-left">
      <RouterLink v-if="!isHomePage" to="/" class="home-button" title="Home">
        <span class="home-icon">&#8592;</span>
      </RouterLink>
    </div>

    <div class="header-center">
      <h1 class="page-title">
        <RouterLink v-if="isHomePage" to="/" class="title-link">{{ pageTitle }}</RouterLink>
        <span v-else>{{ pageTitle }}</span>
      </h1>
      <p v-if="isHomePage" class="page-subtitle">Software Engineer</p>
    </div>

    <div class="header-right">
      <template v-if="!isAuthCallback && !auth.loading">
        <div v-if="auth.isLoggedIn" class="user-inline">
          <img v-if="auth.user?.picture" :src="auth.user.picture" alt="" class="user-avatar" referrerpolicy="no-referrer" />
          <span class="user-name">{{ auth.user?.name }}</span>
          <span class="user-email">&middot; {{ auth.user?.email }}</span>
          <button class="logout-btn" @click="auth.logout">Sign out</button>
        </div>
        <GoogleSignInButton v-else compact />
      </template>
    </div>
  </header>
</template>

<style scoped>
.page-header {
  height: 80px;
  background: #E8E8E8;
  border-bottom: 2px solid #CCCCCC;
  padding: 20px;
  display: grid;
  /* equal side columns keep the title centred whatever the sides contain */
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
}

.header-left {
  display: flex;
  justify-content: flex-start;
}

.header-center {
  text-align: center;
}

.header-right {
  display: flex;
  justify-content: flex-end;
  min-width: 0;
}

.home-button {
  width: 36px;
  height: 36px;
  background: #FFFFFF;
  border: 1px solid #CCCCCC;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: #333333;
}

.home-button:hover {
  background: #F5F5F5;
  border-color: #999999;
}

.home-icon {
  font-size: 20px;
  line-height: 1;
}

.page-title {
  font-size: 24px;
  font-weight: bold;
  color: #333333;
  margin: 0;
  line-height: 40px;
}

.title-link {
  color: inherit;
  text-decoration: none;
}

.title-link:hover {
  text-decoration: underline;
}

.page-subtitle {
  font-size: 14px;
  color: #666666;
  margin: 0;
}

.user-inline {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 14px;
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
}

.user-name {
  font-weight: 600;
  color: #333333;
  white-space: nowrap;
}

.user-email {
  color: #666666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.logout-btn {
  background: #FFFFFF;
  border: 1px solid #CCCCCC;
  border-radius: 4px;
  padding: 6px 12px;
  color: #333333;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}

.logout-btn:hover {
  background: #F5F5F5;
  border-color: #999999;
}

/* Inline gets crowded on small screens: drop the email, then the name */
@media (max-width: 900px) {
  .user-email {
    display: none;
  }
}

@media (max-width: 600px) {
  .page-header {
    padding: 20px 12px;
  }

  .page-title {
    font-size: 18px;
  }

  .user-name {
    display: none;
  }
}
</style>
